import type { CountryCode } from "models/CountryCode";
import type { CustomerPreferences } from "modules/checkout";
import type { UUID } from "utils";
import type CustomerAddress from "./CustomerAddress";
import type { CustomerCredentials } from "./CustomerCredentials";
import type { CustomerPaymentPreferences } from "./CustomerPaymentPreferences";
import type { CustomerProductFlags } from "./CustomerProductFlags";

export const INCORPORATION_DATE_FORMAT = "yyyy-MM-dd";

export const CUSTOMER_DISCRIMINATOR = {
  PSP: "payment_service_provider",
  MERCHANT: "merchant",
  TSP: "technical_service_provider",
  VERIFY: "verify",
} as const;

export type CustomerDiscriminator = typeof CUSTOMER_DISCRIMINATOR[keyof typeof CUSTOMER_DISCRIMINATOR];

export const CUSTOMER_DISCRIMINATOR_LIST: CustomerDiscriminator[] = [
  CUSTOMER_DISCRIMINATOR.PSP,
  CUSTOMER_DISCRIMINATOR.TSP,
  CUSTOMER_DISCRIMINATOR.MERCHANT,
  CUSTOMER_DISCRIMINATOR.VERIFY,
];

export const EDIT_CUSTOMER_DISCRIMINATOR_LIST: {
  [key in CustomerDiscriminator]: CustomerDiscriminator[];
} = {
  [CUSTOMER_DISCRIMINATOR.PSP]: [
    CUSTOMER_DISCRIMINATOR.PSP,
    CUSTOMER_DISCRIMINATOR.MERCHANT,
    CUSTOMER_DISCRIMINATOR.VERIFY,
  ],
  [CUSTOMER_DISCRIMINATOR.MERCHANT]: [
    CUSTOMER_DISCRIMINATOR.PSP,
    CUSTOMER_DISCRIMINATOR.MERCHANT,
    CUSTOMER_DISCRIMINATOR.VERIFY,
  ],
  [CUSTOMER_DISCRIMINATOR.TSP]: [
    CUSTOMER_DISCRIMINATOR.TSP,
    CUSTOMER_DISCRIMINATOR.MERCHANT,
  ],
  [CUSTOMER_DISCRIMINATOR.VERIFY]: [
    CUSTOMER_DISCRIMINATOR.PSP,
    CUSTOMER_DISCRIMINATOR.MERCHANT,
    CUSTOMER_DISCRIMINATOR.VERIFY,
  ],
};

export type CustomerNames = Record<UUID, string>;

export interface Customer {
  id: UUID;
  active: boolean;
  address?: CustomerAddress | null;
  children?: Customer[];
  descendantsCount: number;
  // TODO: should be require after Circuit Breaker feature flag remove
  circuitBreakerEnabled?: boolean;
  productFlags: CustomerProductFlags;
  companyRegistrationNumber: string | null;
  countryCode: CountryCode;
  createdAt: string;
  credentials?: CustomerCredentials;
  discr: CustomerDiscriminator;
  documentNumber: string | null;
  incorporationDate: string | null;
  legalName: string | null;
  merchantCategoryCode?: string | null;
  name: string;
  parent?: UUID | null;
  paymentPreferences?: CustomerPaymentPreferences;
  preferences?: CustomerPreferences;
  parentCustomer?: {
    id: UUID;
    name: string;
  };
  parentAccounts?: UUID[];
  terminateAt?: string | null;
}

export const PROPERTY_CREDENTIALS = "credentials";
export const PROPERTY_MERCHANT_CATEGORY_CODE = "merchantCategoryCode";
export const PROPERTY_ADDRESS = "address";
export const PROPERTY_ACTIVE = "active";
export const PROPERTY_PAYMENT_PREFERENCES = "paymentPreferences";
export const PROPERTY_TYPE = "discr";
export const PROPERTY_CIRCUIT_BREAKER_ENABLED = "circuitBreakerEnabled";
export const PROPERTY_TERMINATE_AT = "terminateAt";
export const PROPERTY_SANDBOX_VERIFY = "sandbox.verify";
export const PROPERTY_PRODUCTION_VERIFY = "production.verify";
export const PROPERTY_SANDBOX_TRANSFORMER = "sandbox.transformer";
export const PROPERTY_PRODUCTION_TRANSFORMER = "production.transformer";
export const PROPERTY_SANDBOX_REFUND = "sandbox.connectRefund";
export const PROPERTY_PRODUCTION_REFUND = "production.connectRefund";
export const PROPERTY_SANDBOX_PAY_BY_LINK = "sandbox.payByLink";
export const PROPERTY_PRODUCTION_PAY_BY_LINK = "production.payByLink";
export const PROPERTY_SANDBOX_PAYOUTS = "sandbox.connectPayouts";
export const PROPERTY_PRODUCTION_PAYOUTS = "production.connectPayouts";
export const PROPERTY_SANDBOX_GATEWAY = "sandbox.gateway";
export const PROPERTY_PRODUCTION_GATEWAY = "production.gateway";
